import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Computer as ComputerIcon,
  Print as PrintIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

const BridgeInstallModal = ({ open, onClose, downloadProgress, onDownload }) => {
  const handleDownload = () => {
    onDownload();
  };

  const getDownloadButtonContent = () => {
    if (!downloadProgress) {
      return {
        icon: <DownloadIcon />,
        text: 'Baixar Bridge Automaticamente'
      };
    }

    switch (downloadProgress.status) {
      case 'fetching':
        return {
          icon: <CircularProgress size={20} color="inherit" />,
          text: 'Buscando última versão...'
        };
      case 'downloading':
        return {
          icon: <DownloadIcon />,
          text: `Baixando ${downloadProgress.fileName}...`
        };
      case 'completed':
        return {
          icon: <CheckCircleIcon />,
          text: 'Download concluído!'
        };
      case 'error':
        return {
          icon: <ErrorIcon />,
          text: 'Erro no download'
        };
      default:
        return {
          icon: <DownloadIcon />,
          text: 'Baixar Bridge Automaticamente'
        };
    }
  };

  const isDownloading = downloadProgress?.status === 'fetching' || downloadProgress?.status === 'downloading';
  const downloadCompleted = downloadProgress?.status === 'completed';
  const downloadError = downloadProgress?.status === 'error';
  const buttonContent = getDownloadButtonContent();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ComputerIcon sx={{ color: '#1976d2', fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
            Instalação do Wala Print Bridge
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            O <strong>Wala Print Bridge</strong> é necessário para conectar diretamente com as impressoras em sua rede local.
          </Typography>
        </Alert>

        {downloadProgress?.status === 'downloading' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Baixando: {downloadProgress.fileName}
            </Typography>
            <LinearProgress 
              variant="indeterminate" 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                }
              }} 
            />
          </Box>
        )}

        {downloadCompleted && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Download concluído!</strong> Verifique sua pasta de downloads e execute o instalador.
            </Typography>
          </Alert>
        )}

        {downloadError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Erro no download:</strong> {downloadProgress.error}
            </Typography>
          </Alert>
        )}

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          O que é o Print Bridge?
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
          É um serviço local que permite a comunicação entre o navegador e suas impressoras de etiquetas,
          garantindo impressão direta e rápida sem a necessidade de drivers específicos.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Benefícios:
        </Typography>

        <List dense>
          <ListItem>
            <ListItemIcon>
              <PrintIcon sx={{ color: '#4caf50' }} />
            </ListItemIcon>
            <ListItemText 
              primary="Impressão direta de etiquetas ZPL"
              secondary="Sem necessidade de downloads ou conversões"
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon sx={{ color: '#4caf50' }} />
            </ListItemIcon>
            <ListItemText 
              primary="Detecção automática de impressoras"
              secondary="Encontra automaticamente impressoras Zebra, Epson e outras na rede"
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <ComputerIcon sx={{ color: '#4caf50' }} />
            </ListItemIcon>
            <ListItemText 
              primary="Execução em segundo plano"
              secondary="Funciona silenciosamente como um serviço do Windows"
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Importante:</strong> Após a instalação, reinicie o navegador para que o bridge seja detectado.
          </Typography>
        </Alert>

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Como instalar:
        </Typography>

        <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            1. Clique em "Baixar Bridge Automaticamente" para fazer o download
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            2. Execute o arquivo baixado na pasta de downloads
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            3. Siga as instruções do instalador
          </Typography>
          <Typography variant="body2">
            4. Reinicie o navegador após a instalação
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={isDownloading}>
          Fechar
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={buttonContent.icon}
          disabled={isDownloading || downloadCompleted}
          color={downloadError ? 'error' : downloadCompleted ? 'success' : 'primary'}
          sx={{ ml: 2 }}
        >
          {buttonContent.text}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BridgeInstallModal;
